// src/pages/students/details/BulkEnrollmentStatusModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useStudents } from '../../../hooks/useStudents';
import { useIntakes } from '../../../hooks/useIntakes';
import { useEnrollments } from '../../../hooks/useEnrollments';

export default function BulkEnrollmentStatusModal({
  studentId,
  isOpen,
  onClose,
  paidEnrollmentIds = [],
}) {
  const { getStudentEnrollments } = useStudents();
  const { intakes } = useIntakes();
  const { bulkUpdateEnrollmentStatus } = useEnrollments();

  const [selectedIntakeId, setSelectedIntakeId] = useState('');
  const [targetStatus, setTargetStatus] = useState('Completed'); // 'Completed' | 'Withdrawn'
  const [selectedIds, setSelectedIds] = useState([]);
  const [tableStats, setTableStats] = useState({
    total: 0,
    eligible: 0,
    ineligible: 0,
  });

  const paidSet = useMemo(
    () => new Set((paidEnrollmentIds || []).map(String)),
    [paidEnrollmentIds]
  );

  // Fetch enrollments for this student (reuse same queryKey as EnrollmentsTab)
  const {
    data: enrollments = [],
    isLoading: enrollmentsLoading,
    isFetching: enrollmentsFetching,
  } = useQuery({
    queryKey: ['enrollments', studentId],
    queryFn: async () => {
      const res = await getStudentEnrollments(studentId);
      if (res?.success) return res.data || [];
      console.error('Bulk modal failed to fetch enrollments:', res?.error);
      return [];
    },
    enabled: isOpen && !!studentId,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    staleTime: 0,
    placeholderData: (prev) => prev,
  });

  // Build intake options from enrollments (only intakes this student actually has)
  const intakeOptions = useMemo(() => {
    const map = new Map();
    (enrollments || []).forEach((enr) => {
      let intake = enr.intake;
      if (!intake && enr.intake_id) {
        intake = intakes.find((i) => i.id === enr.intake_id);
      }
      if (intake && !map.has(intake.id)) {
        map.set(intake.id, intake);
      }
    });

    const arr = Array.from(map.values());
    // sort earliest start_date first
    arr.sort((a, b) => {
      const dateA = a.start_date ? new Date(a.start_date) : new Date(0);
      const dateB = b.start_date ? new Date(b.start_date) : new Date(0);
      return dateA - dateB; // earlier dates come first
    });
    return arr;
  }, [enrollments, intakes]);

  // When modal opens or intakeOptions change, set default intake (latest) & reset targetStatus + selection
  useEffect(() => {
    if (!isOpen) {
      setSelectedIntakeId('');
      setTargetStatus('Completed');
      setSelectedIds([]);
      setTableStats({ total: 0, eligible: 0, ineligible: 0 });
      return;
    }

    if (!selectedIntakeId && intakeOptions.length > 0) {
      setSelectedIntakeId(intakeOptions[0].id);
    }
  }, [isOpen, intakeOptions, selectedIntakeId]);

  if (!isOpen) return null;

  const intakeLoading = enrollmentsLoading || enrollmentsFetching;
  const applyDisabled =
    !selectedIntakeId ||
    selectedIds.length === 0 ||
    bulkUpdateEnrollmentStatus.isLoading;

  const handleApply = async () => {
    if (applyDisabled) return;

    try {
      const result = await bulkUpdateEnrollmentStatus.mutateAsync({
        studentId,
        intakeId: selectedIntakeId,
        enrollmentIds: selectedIds,
        targetStatus,
      });

      // result: { updated_count, skipped_count, results: [...] }
      if (result) {
        const { updated_count, skipped_count } = result;
        let msg = `Bulk update complete.\nUpdated: ${updated_count}\nSkipped: ${skipped_count}`;
        window.alert(msg);
      }

      onClose();
    } catch (err) {
      console.error('Bulk update failed:', err);
      window.alert(err.message || 'Bulk update failed. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal container */}
      <div className="relative z-50 w-full h-full max-w-6xl mx-auto bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Bulk Update Enrollments
            </h2>
            <p className="text-sm text-gray-500">
              Student ID:{' '}
              <span className="font-mono text-gray-700">{studentId}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top filter bar */}
          <div className="border-b border-gray-200 px-6 py-4 flex flex-wrap items-center gap-4">
            {/* Intake selector */}
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Intake / Enrollment Year
              </label>
              <select
                className="block w-56 rounded-md border-gray-300 shadow-sm text-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={selectedIntakeId || ''}
                onChange={(e) => setSelectedIntakeId(e.target.value)}
                disabled={intakeLoading || intakeOptions.length === 0}
              >
                {intakeOptions.length === 0 && (
                  <option value="">No intakes available</option>
                )}
                {intakeOptions.length > 0 &&
                  intakeOptions.map((intake) => {
                    const start =
                      intake.start_date &&
                      new Date(intake.start_date).toLocaleDateString();
                    const end =
                      intake.end_date &&
                      new Date(intake.end_date).toLocaleDateString();
                    const dateLabel =
                      start && end
                        ? ` (${start} - ${end})`
                        : start
                        ? ` (${start})`
                        : '';
                    return (
                      <option key={intake.id} value={intake.id}>
                        {intake.name}
                        {dateLabel}
                      </option>
                    );
                  })}
              </select>
              {intakeLoading && (
                <span className="mt-1 text-xs text-gray-400">
                  Loading intakes…
                </span>
              )}
            </div>

            {/* Target status selector */}
            <div className="flex flex-col">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                New Status
              </span>
              <div className="inline-flex items-center gap-4 text-sm">
                <label className="inline-flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="targetStatus"
                    value="Completed"
                    checked={targetStatus === 'Completed'}
                    onChange={() => setTargetStatus('Completed')}
                    className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  />
                  <span>Completed</span>
                </label>
                <label className="inline-flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="targetStatus"
                    value="Withdrawn"
                    checked={targetStatus === 'Withdrawn'}
                    onChange={() => setTargetStatus('Withdrawn')}
                    className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  />
                  <span>Withdrawn</span>
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500 max-w-md">
                Only <span className="font-semibold">Active</span> subjects will
                be updated.
                {targetStatus === 'Completed' && (
                  <>
                    {' '}
                    To mark as{' '}
                    <span className="font-semibold">Completed</span>, subjects
                    must have at least one{' '}
                    <span className="font-semibold">Paid</span> payment.
                  </>
                )}
                {targetStatus === 'Withdrawn' && (
                  <>
                    {' '}
                    Withdrawn does not check payment status.
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Main content */}
          <div className="flex-1 px-6 py-4 overflow-auto">
            {intakeLoading ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                Loading enrollment data…
              </div>
            ) : intakeOptions.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm text-center">
                This student has no enrollments with intake information yet.
                <br />
                Bulk update is not available.
              </div>
            ) : !selectedIntakeId ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                Please select an intake to continue.
              </div>
            ) : (
              <SubjectsTable
                enrollments={enrollments}
                intakeId={selectedIntakeId}
                targetStatus={targetStatus}
                paidSet={paidSet}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                onStatsChange={setTableStats}
              />
            )}
          </div>

          {/* Sticky footer */}
          <div className="border-t border-gray-200 px-6 py-3 flex items-center justify-between text-sm">
            <div className="text-gray-600">
              <span className="font-semibold">{selectedIds.length}</span> selected
              {' | '}
              <span className="font-semibold">{tableStats.eligible}</span> eligible
              {' | '}
              <span className="font-semibold">{tableStats.ineligible}</span> ineligible
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={applyDisabled}
                className={`inline-flex items-center px-4 py-1.5 rounded-md text-sm font-medium text-white ${
                  applyDisabled
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {bulkUpdateEnrollmentStatus.isLoading ? 'Updating…' : 'Apply Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                               Subjects Table                               */
/* -------------------------------------------------------------------------- */

function SubjectsTable({
  enrollments,
  intakeId,
  targetStatus,
  paidSet,
  selectedIds,
  onSelectionChange,
  onStatsChange,
}) {
  // Filter to this intake only
  const intakeEnrollments = useMemo(
    () => enrollments.filter((e) => e.intake_id === intakeId),
    [enrollments, intakeId]
  );

  // Determine eligibility for each subject
  const rows = useMemo(() => {
    return intakeEnrollments.map((e) => {
      const isActive = e.status === 'Active';
      const isPaid = paidSet.has(String(e.id));

      let eligible = false;
      let reason = '';

      if (!isActive) {
        eligible = false;
        reason = 'Subject is not Active';
      } else if (targetStatus === 'Completed' && !isPaid) {
        eligible = false;
        reason = 'Must be Paid to mark as Completed';
      } else {
        eligible = true;
      }

      return {
        ...e,
        isPaid,
        eligible,
        reason,
      };
    });
  }, [intakeEnrollments, paidSet, targetStatus]);

  // Auto-select all eligible rows whenever rows/targetStatus/intake changes
  useEffect(() => {
    const eligibleIds = rows.filter((r) => r.eligible).map((r) => r.id);
    onSelectionChange(eligibleIds);

    const total = rows.length;
    const eligibleCount = eligibleIds.length;
    const ineligibleCount = total - eligibleCount;
    onStatsChange({ total, eligible: eligibleCount, ineligible: ineligibleCount });
  }, [rows, targetStatus, intakeId, onSelectionChange, onStatsChange]);

  const toggleRow = (id, eligible) => {
    if (!eligible) return;
    onSelectionChange((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const getStatusBadgeClasses = (status) => {
    if (status === 'Active') return 'bg-yellow-100 text-yellow-800';
    if (status === 'Completed') return 'bg-green-100 text-green-800';
    if (status === 'Withdrawn') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  if (!rows.length) {
    return (
      <div className="h-full flex items-center justify-center text-gray-400 text-sm">
        No subjects found for this intake.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Table header */}
      <div className="grid grid-cols-12 font-medium text-gray-700 border-b pb-2">
        <div className="col-span-1" />
        <div className="col-span-5">Subject</div>
        <div className="col-span-3">Current Status</div>
        <div className="col-span-3">Payment</div>
      </div>

      {/* Rows */}
      {rows.map((row) => {
        const disabled = !row.eligible;
        const checked = selectedIds.includes(row.id);

        return (
          <div
            key={row.id}
            className={`grid grid-cols-12 py-2 items-center border-b text-sm ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {/* Checkbox */}
            <div className="col-span-1 flex justify-center">
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => toggleRow(row.id, row.eligible)}
                className="h-4 w-4 text-indigo-600 border-gray-300"
              />
            </div>

            {/* Subject name */}
            <div className="col-span-5">
              <div className="font-medium text-gray-900">
                {row.subject?.code} - {row.subject?.name}
              </div>
              {disabled && (
                <div className="text-xs text-red-500">{row.reason}</div>
              )}
            </div>

            {/* Current status */}
            <div className="col-span-3">
              <span
                className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClasses(
                  row.status
                )}`}
              >
                {row.status}
              </span>
            </div>

            {/* Payment pill */}
            <div className="col-span-3">
              <span
                className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                  row.isPaid ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {row.isPaid ? 'Paid' : 'Not Paid'}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
